import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import UploadThumbnail from "../../utils/UploadImg";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import { enqueueSnackbar } from "notistack";
import {
  sendPutRequest,
  uploadVideoThumbnail,
} from "../../services/ContentServices";
import {
  ISubscription,
  Savesubscription,
} from "../../services/SubscriptionServices";
import { capitalizeFirstLetter } from "../../utils/commons";

interface Privileges {
  gymMembership: boolean;
  fullContentAccess: boolean;
  customisedDietPlan: boolean;
  playListAccess: boolean;
  onlineMembership: boolean;
}

interface FormState {
  thumbnail: File | null;
  name: string;
  currThumbnailUrl: string;
  price: number;
  discount: number;
  benefits: string[];
  description: string;
  duration: number;
  maxPausePeriodAllowed: number;
  privileges: Privileges;
}

interface CreateSubscriptionProps {
  setShowCreate: React.Dispatch<React.SetStateAction<boolean>>;
  clonedData: ISubscription | null;
  onEmptyClone: () => void;
}

const CreateSubscription: React.FC<CreateSubscriptionProps> = ({
  setShowCreate,
  clonedData,
  onEmptyClone,
}) => {
  const theme = useTheme();
  const presetDurations = [30, 60, 90, 180, 270, 366, -1];
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isCustomDurationSelected, setIsCustomDurationSelected] =
    useState<boolean>(false);
  const [customDurationDays, setCustomDurationDays] = useState<string>("");
  const [formState, setFormState] = useState<FormState>({
    thumbnail: null as File | null,
    name: "",
    currThumbnailUrl: "",
    price: 0,
    discount: 0,
    benefits: Array(4).fill(""),
    description: "",
    duration: 30,
    maxPausePeriodAllowed: 0,
    privileges: {
      gymMembership: false,
      fullContentAccess: false,
      customisedDietPlan: false,
      playListAccess: false,
      onlineMembership: false,
    },
  });

  const resetForm = () => {
    setFormState({
      thumbnail: null,
      name: "",
      currThumbnailUrl: "",
      price: 0,
      discount: 0,
      benefits: Array(4).fill(""),
      description: "",
      duration: 30,
      maxPausePeriodAllowed: 0,
      privileges: {
        gymMembership: false,
        fullContentAccess: false,
        customisedDietPlan: false,
        playListAccess: false,
        onlineMembership: false,
      },
    });
    setIsCustomDurationSelected(false);
    setCustomDurationDays("");
  };

  useEffect(() => {
    if (clonedData) {
      setFormState({
        thumbnail: null,
        name: clonedData.name,
        currThumbnailUrl: clonedData.thumbnailUrl as string,
        price: clonedData.price,
        discount: clonedData.discount,
        benefits: [
          ...clonedData.benefits,
          ...Array(4 - clonedData.benefits.length).fill(""),
        ],
        description: clonedData.shortDescription,
        duration: clonedData.duration,
        maxPausePeriodAllowed: clonedData.maxPausePeriodAllowed || 0,
        privileges: {
          gymMembership: clonedData.gymMembership,
          fullContentAccess: clonedData.hasFullContentAccess,
          customisedDietPlan: clonedData.canRequestDietPlan,
          playListAccess: clonedData.hasAllotedContent,
          onlineMembership: clonedData.onlineMembership,
        },
      });

      const isPresetDuration = presetDurations.includes(clonedData.duration);
      setIsCustomDurationSelected(!isPresetDuration);
      setCustomDurationDays(
        !isPresetDuration ? String(clonedData.duration) : ""
      );
    }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]:
        name === "name" || name === "description"
          ? value.charAt(0).toUpperCase() + value.slice(1)
          : value,
    }));
  };

  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const parsedValue = Number(value);

    setFormState((prev) => ({
      ...prev,
      [name]: value === "" || Number.isNaN(parsedValue) ? 0 : parsedValue,
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    const updatedPrivileges = { ...formState.privileges };

    if (name === "gymMembership") {
      updatedPrivileges.gymMembership = checked;
      updatedPrivileges.fullContentAccess = checked;
      updatedPrivileges.customisedDietPlan = checked;
      updatedPrivileges.playListAccess = checked;
      updatedPrivileges.onlineMembership = checked;
    } else if (name === "fullContentAccess") {
      updatedPrivileges.fullContentAccess = checked;
      updatedPrivileges.playListAccess = checked;
    } else if (name === "onlineMembership") {
      updatedPrivileges.onlineMembership = checked;
      updatedPrivileges.customisedDietPlan = checked;
    } else {
      updatedPrivileges[name as keyof typeof updatedPrivileges] = checked;
    }

    // Disable privileges that are covered by other selected privileges
    if (updatedPrivileges.gymMembership) {
      updatedPrivileges.fullContentAccess = true;
      updatedPrivileges.customisedDietPlan = true;
      updatedPrivileges.playListAccess = true;
    } else if (updatedPrivileges.fullContentAccess) {
      updatedPrivileges.playListAccess = true;
    } else if (updatedPrivileges.onlineMembership) {
      updatedPrivileges.customisedDietPlan = true;
    }

    setFormState((prev) => ({
      ...prev,
      privileges: updatedPrivileges,
    }));
  };

  const handleDurationSelect = (value: number | "custom") => {
    if (value === "custom") {
      setIsCustomDurationSelected(true);
      return;
    }

    setIsCustomDurationSelected(false);
    setCustomDurationDays("");
    setFormState((prev) => ({ ...prev, duration: value }));
  };

  const handleCustomDurationChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setCustomDurationDays(value);

    const parsedDuration = Number(value);
    if (!Number.isNaN(parsedDuration) && parsedDuration > 0) {
      setFormState((prev) => ({ ...prev, duration: parsedDuration }));
    }
  };

  const inputStyle = {
    marginTop: theme.spacing(1),
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      fontSize: "1rem",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: theme.palette.background.default,
    },
  };

  const durationOptions: Array<{ value: number | "custom"; label: string }> = [
    { value: 30, label: "30 days" },
    { value: 60, label: "60 days" },
    { value: 90, label: "90 days" },
    { value: 180, label: "180 days" },
    { value: 270, label: "270 days" },
    { value: 366, label: "366 days (1 year)" },
    { value: -1, label: "Life time" },
    { value: "custom", label: "Custom" },
  ];

  const sectionCardSx = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    p: 2,
  };

  const handleCreateSubScription = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!formState.name) {
        return enqueueSnackbar("Please fill Name");
      }

      if (!formState.thumbnail && !clonedData) {
        return enqueueSnackbar("Thumbnail is required");
      }

      if (isCustomDurationSelected) {
        const customDays = Number(customDurationDays);
        if (!customDurationDays || Number.isNaN(customDays) || customDays < 1) {
          return enqueueSnackbar("Custom duration must be at least 1 day", {
            variant: "warning",
          });
        }
      }

      if (formState.maxPausePeriodAllowed < 0) {
        return enqueueSnackbar("Max pause period cannot be negative", {
          variant: "warning",
        });
      }

      setIsUploading(true);

      let thumbAwsUrl = clonedData?.thumbnailUrl;
      let putStatus;

      if (formState.thumbnail) {
        const { data } = await uploadVideoThumbnail(formState.thumbnail.name);
        const signUrl = data?.data as string;
        thumbAwsUrl = signUrl.split("?")[0];

        putStatus = await sendPutRequest({
          signUrl,
          file: formState.thumbnail,
        });
      }

      if (putStatus?.status === 200 || clonedData) {
        const filteredBenefits = formState.benefits.filter(
          (benefit) => benefit.trim() !== ""
        );

        const body = {
          name: capitalizeFirstLetter(formState.name) as string,
          price: formState.price as number,
          duration: formState.duration as number,
          benefits: filteredBenefits.map((benefit) =>
            capitalizeFirstLetter(benefit)
          ),
          shortDescription: capitalizeFirstLetter(
            formState.description
          ) as string,
          gymMembership: formState.privileges.gymMembership as boolean,
          hasFullContentAccess: formState.privileges
            .fullContentAccess as boolean,
          canRequestDietPlan: formState.privileges
            .customisedDietPlan as boolean,
          discount: formState.discount as number,
          hasAllotedContent: formState.privileges.playListAccess as boolean,
          onlineMembership: formState.privileges.onlineMembership as boolean,
          maxPausePeriodAllowed: formState.maxPausePeriodAllowed as number,
          thumbnailUrl: thumbAwsUrl as string,
        };

        const response = await Savesubscription(body);

        if (response === 201) {
          if (clonedData) {
            onEmptyClone();
            enqueueSnackbar("Subscription Cloned successfully");
          } else {
            enqueueSnackbar("Subscription created successfully");
          }

          resetForm();
          setShowCreate(false);
        } else {
          enqueueSnackbar("Failed to create Subscription", {
            variant: "error",
          });
        }
      }

      setIsUploading(false);
    } catch (error) {
      console.error("Error:", error);
      setIsUploading(false);
      enqueueSnackbar("Failed to create playlist", { variant: "error" });
    }
  };

  const handleBack = () => {
    onEmptyClone();
    setShowCreate(false);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
        <IconButton
          edge="start"
          aria-label="back"
          onClick={handleBack}
          sx={{ marginRight: theme.spacing(1), marginLeft: theme.spacing(1) }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h6">Create Subscription</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              id="name"
              variant="outlined"
              name="name"
              placeholder="Enter Subscription Name"
              type="text"
              sx={inputStyle}
              required
              fullWidth
              value={formState.name}
              onChange={handleInputChange}
            />
            <TextField
              id="price"
              variant="outlined"
              name="price"
              placeholder="Enter Price"
              type="number"
              sx={inputStyle}
              required
              fullWidth
              value={formState.price !== 0 ? formState.price : ""}
              onChange={handleNumberChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <b style={{ color: "gray" }}>₹</b>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              id="discount"
              variant="outlined"
              type="number"
              name="discount"
              placeholder="Enter Discount"
              sx={inputStyle}
              required
              fullWidth
              value={formState.discount !== 0 ? formState.discount : ""}
              onChange={handleNumberChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <b style={{ color: "gray" }}>%</b>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              id="description"
              variant="outlined"
              name="description"
              sx={inputStyle}
              multiline
              rows={11}
              placeholder="Enter Short Description"
              fullWidth
              value={formState.description}
              onChange={handleInputChange}
            />
            <UploadThumbnail
              previewUrl={formState.currThumbnailUrl}
              setPreviewUrl={(url) =>
                setFormState((prev) => ({
                  ...prev,
                  currThumbnailUrl: url as string,
                }))
              }
              setFile={(file) =>
                setFormState((prev) => ({
                  ...prev,
                  thumbnail: (file as File) || null,
                }))
              }
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={sectionCardSx}>
              <Typography fontSize={26} fontWeight={600} sx={{ mb: 1.5 }}>
                Duration
              </Typography>

              <Grid container spacing={1.5}>
                {durationOptions.map((option) => {
                  const selected =
                    option.value === "custom"
                      ? isCustomDurationSelected
                      : !isCustomDurationSelected &&
                        formState.duration === option.value;

                  return (
                    <Grid item xs={6} key={option.label}>
                      <Button
                        fullWidth
                        onClick={() => handleDurationSelect(option.value)}
                        sx={{
                          borderRadius: "10px",
                          p: 1.4,
                          textTransform: "none",
                          color: selected
                            ? theme.palette.background.default
                            : theme.palette.text.primary,
                          fontWeight: 700,
                          backgroundColor: selected
                            ? theme.palette.secondary.main
                            : theme.palette.background.paper,
                          border: `1px solid ${theme.palette.secondary.main}`,
                          "&:hover": {
                            backgroundColor: selected
                              ? theme.palette.secondary.main
                              : theme.palette.info.light,
                          },
                        }}
                      >
                        {option.label}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

              {isCustomDurationSelected && (
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    placeholder="Enter custom number of days"
                    type="number"
                    fullWidth
                    value={customDurationDays}
                    onChange={handleCustomDurationChange}
                    sx={inputStyle}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mt: 0.8,
                      ml: 0.2,
                    }}
                  >
                    Enter a custom duration in days
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.5 }}>
                Max Pause Period
              </Typography>
              <TextField
                placeholder="Enter maximum pause period (days)"
                type="number"
                name="maxPausePeriodAllowed"
                fullWidth
                value={
                  formState.maxPausePeriodAllowed !== 0
                    ? formState.maxPausePeriodAllowed
                    : ""
                }
                onChange={handleNumberChange}
                sx={inputStyle}
              />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary, mt: 0.8, ml: 0.2 }}
              >
                Maximum days subscription can be paused
              </Typography>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.5 }}>
                Subscription Benefits
              </Typography>
              {formState.benefits.map((benefit, index) => (
                <TextField
                  key={index}
                  id={`benefit${index + 1}`}
                  variant="outlined"
                  name={`benefit${index}`}
                  placeholder={`Enter benefit ${index + 1}`}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LoyaltyIcon
                          sx={{
                            fontSize: 15,
                            color: theme.palette.secondary.main,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ ...inputStyle, mt: index === 0 ? 0 : 1 }}
                  fullWidth
                  value={benefit}
                  onChange={(e) => {
                    const updatedBenefits = [...formState.benefits];
                    updatedBenefits[index] =
                      e.target.value.charAt(0).toUpperCase() +
                      e.target.value.slice(1);
                    setFormState((prev) => ({
                      ...prev,
                      benefits: updatedBenefits,
                    }));
                  }}
                />
              ))}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="contained" color="primary" onClick={resetForm}>
                Reset
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                disabled={isUploading}
                onClick={handleCreateSubScription}
              >
                {isUploading ? (
                  <CircularProgress
                    style={{ width: "20px", height: "20px" }}
                    color="inherit"
                  />
                ) : (
                  "Save"
                )}
              </Button>
            </Box>

            <Box sx={sectionCardSx}>
              <Typography fontSize={24} fontWeight={600} sx={{ mb: 1.3 }}>
                Privileges
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <FormGroup
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  {Object.keys(formState.privileges).map((privilege) => (
                    <Box
                      key={privilege}
                      sx={{ display: "flex", width: "100%" }}
                    >
                      <FormControlLabel
                        sx={{ width: "100%", mt: 0.4 }}
                        control={
                          <Checkbox
                            checked={
                              formState.privileges[
                                privilege as keyof typeof formState.privileges
                              ]
                            }
                            onChange={handleCheckboxChange}
                            name={privilege}
                            disabled={
                              (privilege === "fullContentAccess" &&
                                formState.privileges.gymMembership) ||
                              (privilege === "customisedDietPlan" &&
                                (formState.privileges.gymMembership ||
                                  (formState.privileges.onlineMembership &&
                                    !formState.privileges.gymMembership))) ||
                              (privilege === "playListAccess" &&
                                (formState.privileges.gymMembership ||
                                  formState.privileges.fullContentAccess)) ||
                              (privilege === "onlineMembership" &&
                                formState.privileges.gymMembership)
                            }
                            sx={{
                              color: theme.palette.text.secondary,
                              "&.Mui-checked": {
                                color: theme.palette.secondary.main,
                              },
                            }}
                          />
                        }
                        label={privilege
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase())}
                      />
                    </Box>
                  ))}
                </FormGroup>
              </FormControl>
            </Box>

            <Box sx={{ ...sectionCardSx, minHeight: 220 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography fontSize={24} fontWeight={600}>
                  Playlists
                </Typography>
                <Button
                  disabled
                  variant="outlined"
                  sx={{
                    color: theme.palette.secondary.main,
                    borderColor: theme.palette.secondary.main,
                    textTransform: "none",
                    opacity: 0.7,
                  }}
                >
                  Manage Playlist
                </Button>
              </Box>
              <Typography sx={{ color: theme.palette.text.secondary }}>
                Playlist management is available after subscription creation.
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreateSubscription;
